/**
 * @author shivam shrivastava 17-06-2025
 * @description Lead Reports Component
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Container,
  Card,
} from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";

const LeadReports = () => {
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  const [body, setBody] = useState([]);
  const [owners, setOwners] = useState([]);
  const [leadStatusArray, setleadStatusArray] = useState(
    JSON.parse(sessionStorage.getItem("lead_status")) || [],
  );
  const [isSpinner, setIsSpinner] = useState(false);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    ownerId: "",
    status: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;
  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole === "USER" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  useEffect(() => {
    // Get user info from token
    try {
      const token = sessionStorage.getItem("token");
      if (token) {
        const decoded = jwt_decode(token);
        setUserInfo(decoded);
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }

    const fetchOwners = async () => {
      const users = await WhatsAppAPI.fetchUsers();
      if (users) setOwners(users);
    };
    fetchOwners();
  }, []);

  const fetchReports = async () => {
    setIsSpinner(false);
    const params = {
      ...filters,
      startDate: filters.startDate
        ? moment(filters.startDate).format("YYYY-MM-DD")
        : undefined,
      endDate: filters.endDate
        ? moment(filters.endDate).format("YYYY-MM-DD")
        : undefined,
      status: filters.status || undefined,
      ownerId: filters.ownerId || undefined,
    };
    const res = await WhatsAppAPI.getLeadReports(params);
    if (res.success) {
      // Add formatted phone numbers to the data
      const formattedData = res.records.map((record) => ({
        ...record,
        display_whatsapp_number: formatPhoneNumber(record.whatsapp_number),
      }));
      setBody(formattedData);
    } else {
      setBody([]);
    }
    setIsSpinner(true);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilter = () => {
    fetchReports();
  };

  // CSV Download utility
  const downloadCSV = (e) => {
    if (!body.length) {
      toast.info("No records to download!", { position: "top-right" });
      e && e.preventDefault && e.preventDefault();
      return;
    }
    const header = [
      "Name",
      "WhatsApp Number",
      "Status",
      "Assigned",
      "Created Date",
    ];
    const rows = body.map((row) => [
      row.leadname,
      `${row.country_code} ${formatPhoneNumber(row.whatsapp_number)}`, // Apply formatting for CSV
      row.leadstatus,
      row.ownername,
      moment(row.createddate).format("DD-MM-YYYY"),
    ]);
    const csvContent = [
      header.join(","),
      ...rows.map((r) =>
        r.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "lead_reports.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(body.length / pageSize);
  const paginatedData = body.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  // testing git

  return (
    <>
      <ToastContainer />
      <Container className="d-flex justify-content-center align-items-start mt-4 mb-5">
        <Card
          className="shadow-sm border-0 rounded-3 p-4 w-100"
          style={{ maxWidth: 900 }}
        >
          <h5 className="mb-3 fw-bold text-center mb-3">Lead Reports</h5>
          <Row className="mb-3 align-items-end">
            <Col md={4} sm={6} xs={12} className="mb-2">
              <Form.Label>Start Date</Form.Label>
              <DatePicker
                selected={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                className="form-control"
                dateFormat="dd-MM-yyyy"
                placeholderText="Start Date"
                isClearable
              />
            </Col>
            <Col md={4} sm={6} xs={12} className="mb-2">
              <Form.Label>End Date</Form.Label>
              <DatePicker
                selected={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                className="form-control"
                dateFormat="dd-MM-yyyy"
                placeholderText="End Date"
                isClearable
              />
            </Col>
            {userInfo.userrole !== "USER" && (
              <>
                <Col md={4} sm={6} xs={12} className="mb-2">
                  <Form.Label>Owner</Form.Label>
                  <Form.Select
                    value={filters.ownerId}
                    onChange={(e) =>
                      handleFilterChange("ownerId", e.target.value)
                    }
                  >
                    <option value="">All Owners</option>
                    {owners.map((owner) => (
                      <option value={owner.id} key={owner.id}>
                        {owner.firstname} {owner.lastname}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </>
            )}
            <Col md={4} sm={6} xs={12} className="mb-2">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Status</option>
                {leadStatusArray.map((item, idx) => (
                  <option value={item.label} key={idx}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} sm={6} xs={12} className="mb-2 d-flex align-items-end">
              <Button
                variant="primary"
                className="me-2 w-100"
                onClick={handleFilter}
              >
                Filter
              </Button>
              {(userInfo.userrole !== "USER" ||
                userInfo.can_export_leads === true) && (
                <Button
                  variant="success"
                  className="w-100"
                  onClick={downloadCSV}
                  disabled={!body.length}
                >
                  Download
                </Button>
              )}
            </Col>
          </Row>
          <div className="table-responsive">
            <Table
              striped
              bordered
              className="related-list-table"
              responsive="sm"
            >
              <thead>
                <tr>
                  <th>Name</th>
                  <th>WhatsApp Number</th>
                  <th>Status</th>
                  <th>Assigned</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {isSpinner && body.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.leadname}</td>
                      <td>
                        {row.country_code}{" "}
                        {row.display_whatsapp_number ||
                          formatPhoneNumber(row.whatsapp_number)}
                      </td>
                      <td>{row.leadstatus}</td>
                      <td>{row.ownername}</td>
                      <td>{moment(row.createddate).format("DD-MM-YYYY")}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {isSpinner ? "No records found" : "Loading..."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex justify-content-center my-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="me-2"
              >
                Previous
              </Button>
              <span className="align-self-center">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="ms-2"
              >
                Next
              </Button>
            </div>
          )}
        </Card>
      </Container>
    </>
  );
};
export default LeadReports;
